import React, { useState } from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Button, Grid } from '@mui/material';

import { PropertyTextInput, TooltipIconButton } from '@paragon/client-tools';
import { isFeaturePatternTruss, isProfileTruss } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField } from '@paragon/material';

import { finishInsertGusset } from '../../store/actions/insertActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import {
  applicationOnPickingGussetLocation,
  applicationOnSetInsertionGussetCenterPoint,
} from '../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import InsertionPanelContainer from '../interface/subpanels/insertions/InsertionPanelContainer';

const pickPointHelpText =
  'Pick a point for the center of the gusset. The generated gusset will be aligned with the closest perimeter edge ' +
  'of the truss and cut against other perimeter edges.';

export function GussetCreationDetails() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);
  if (!insertingObjectInfo || insertingObjectInfo.type !== 'gusset')
    throw new Error('Gusset creation UI while not inserting gusset');
  const { centerPoint } = insertingObjectInfo;
  const isPickingGussetPoint = useAppSelector((state) => getPickingObjectInfo(state)?.type === 'pickingGussetLocation');
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const defaultHeight =
    activeComponentDesign !== null &&
    isFeaturePatternTruss(activeComponentDesign.component) &&
    activeComponentDesign.component.inferredSettings
      ? activeComponentDesign.component.inferredSettings.depth
      : 2 * 12;
  const defaultMaterial =
    activeComponentDesign !== null
      ? isProfileTruss(activeComponentDesign.component)
        ? '7/16" 24/16 span rated OSB'
        : '23/32" 48/24 or 24 OC span rated OSB'
      : '';

  const [height, setHeight] = useState<number>(defaultHeight);
  const [width, setWidth] = useState<number>(4 * 12);
  const [material, setMaterial] = useState<string>(defaultMaterial);

  return (
    <InsertionPanelContainer
      disabled={centerPoint === null}
      onInsert={() => void dispatch(finishInsertGusset(height, width, material))}
    >
      <PropertiesPanel title="Insert Gusset">
        <Grid container spacing={1} sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <Grid size={4}>
            <DistanceField
              label="X"
              value={centerPoint?.x ?? 0}
              onSubmit={(newX) => {
                dispatch(applicationOnSetInsertionGussetCenterPoint({ ...centerPoint!, x: newX }));
              }}
              disabled={centerPoint === null}
            />
          </Grid>
          <Grid size={4}>
            <DistanceField
              label="Y"
              value={centerPoint?.y ?? 0}
              onSubmit={(newY) => {
                dispatch(applicationOnSetInsertionGussetCenterPoint({ ...centerPoint!, y: newY }));
              }}
              disabled={centerPoint === null}
            />
          </Grid>
          <Grid size={3}>
            <Button
              variant="outlined"
              sx={{ width: '100%', mt: 1 }}
              disabled={isPickingGussetPoint}
              onClick={() => {
                dispatch(applicationOnSetInsertionGussetCenterPoint(null));
                dispatch(applicationOnPickingGussetLocation());
              }}
            >
              Pick{isPickingGussetPoint ? 'ing' : ''} Point
            </Button>
          </Grid>
          <Grid size={1} sx={{ mt: 1 }}>
            <TooltipIconButton title={pickPointHelpText}>
              <HelpIcon />
            </TooltipIconButton>
          </Grid>
        </Grid>
        <DistanceField label="Height" value={height} onSubmit={setHeight} />
        <DistanceField label="Width" value={width} onSubmit={setWidth} />
        <PropertyTextInput property="Material" value={material} submit={setMaterial} />
      </PropertiesPanel>
    </InsertionPanelContainer>
  );
}
